/**
 * React context provider for tracking SDK
 * 
 * Responsibilities:
 * - Initialize EventQueue with IndexedDB and fallback handling
 * - Fetch configuration from backend on mount
 * - Create and manage BatchManager lifecycle
 * - Provide tracking context to children
 * 
 * Requirements: 1.1, 1.2, 1.3, 14.4
 */

import React, { createContext, useState, useEffect, useRef } from 'react';
import { EventQueue } from '../core/EventQueue';
import { BatchManager, BatchManagerConfig } from '../core/BatchManager';
import { IndexedDBAdapter } from '../storage/IndexedDBAdapter';
import { LocalStorageAdapter } from '../storage/LocalStorageAdapter';
import { NoOpAdapter } from '../storage/NoOpAdapter';
import { TrackingConfig, TrackingProviderProps, UserEnrichmentContext } from '../types/config';

/**
 * Context value provided to children components
 */
export interface TrackingContextValue {
  /** Event queue instance */
  queue: EventQueue | null;
  /** Batch manager instance */
  batchManager: BatchManager | null;
  /** Current configuration */
  config: TrackingConfig | null;
  /** Whether tracking is enabled */
  isEnabled: boolean;
  /** App name identifier */
  appName: string;
  /** User enrichment context - injected into every event */
  userEnrichment: UserEnrichmentContext;
  /** Event types that are suppressed and will not be queued */
  disabledEventTypes: string[];
  /** Refresh configuration from backend */
  refreshConfig: () => Promise<void>;
}

/**
 * React context for tracking functionality
 */
export const TrackingContext = createContext<TrackingContextValue | null>(null);

/**
 * Default configuration values
 * Used when backend fetch fails or returns incomplete data
 * 
 * Requirement 1.3: Default configuration fallback
 */
const DEFAULT_CONFIG: TrackingConfig = {
  enabled: true,
  samplingRate: 1.0,
  rateLimit: 1000,
  allowlist: ['session_id', 'app_name', 'event_type', 'timestamp'],
  batchSize: 20,
  flushInterval: 60000, // 60 seconds
};

/**
 * TrackingProvider component
 * 
 * Initializes the tracking SDK and provides context to children.
 * On mount, it:
 * 1. Initializes EventQueue with storage adapter fallback chain
 * 2. Fetches configuration from backend API
 * 3. Creates BatchManager with configured thresholds
 * 4. Starts batch transmission
 * 
 * Requirements: 1.1, 1.2, 1.3, 14.4
 */
export const TrackingProvider: React.FC<TrackingProviderProps> = ({
  appName,
  apiBaseUrl,
  authToken,
  enabled = true,
  defaultSamplingRate,
  batchSize,
  flushInterval,
  userEnrichment = {},
  disabledEventTypes = [],
  children,
}) => {
  // State
  const [queue, setQueue] = useState<EventQueue | null>(null);
  const [batchManager, setBatchManager] = useState<BatchManager | null>(null);
  const [config, setConfig] = useState<TrackingConfig | null>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(enabled);

  // Refs to track initialization and cleanup
  const isInitialized = useRef(false);
  const isUnmounting = useRef(false);

  /**
   * React to changes in the enabled prop
   * This allows parent components to dynamically enable/disable tracking
   */
  useEffect(() => {
    if (config && batchManager) {
      // Update isEnabled based on both prop and backend config
      const shouldBeEnabled = enabled && config.enabled;
      setIsEnabled(shouldBeEnabled);
      
      console.log('[TrackingProvider] Enabled state changed:', {
        enabled,
        configEnabled: config.enabled,
        shouldBeEnabled
      });
      
      // Note: BatchManager start/stop is idempotent - calling multiple times is safe
      if (shouldBeEnabled) {
        console.log('[TrackingProvider] Ensuring BatchManager is started');
        batchManager.start();
      } else {
        console.log('[TrackingProvider] Ensuring BatchManager is stopped');
        batchManager.stop();
      }
    }
  }, [enabled, config, batchManager]);

  /**
   * Initialize EventQueue with storage adapter fallback
   * 
   * Fallback chain:
   * 1. IndexedDBAdapter (persistent, best performance)
   * 2. LocalStorageAdapter (persistent, limited capacity)
   * 3. NoOpAdapter (no persistence, graceful degradation)
   * 
   * Requirements: 1.6, 1.7, 1.8
   */
  const initializeQueue = async (): Promise<EventQueue> => {
    // Try IndexedDB first
    if (typeof window !== 'undefined' && window.indexedDB) {
      try {
        const indexedDBQueue = new IndexedDBAdapter();
        // Test IndexedDB availability by attempting to get size
        await indexedDBQueue.size();
        console.log('[TrackingProvider] Using IndexedDB storage adapter');
        return indexedDBQueue;
      } catch (error) {
        console.warn('[TrackingProvider] IndexedDB unavailable, falling back to localStorage:', error);
      }
    }

    // Try localStorage as fallback
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const localStorageQueue = new LocalStorageAdapter();
        // Test localStorage availability
        await localStorageQueue.size();
        console.log('[TrackingProvider] Using localStorage storage adapter');
        return localStorageQueue;
      } catch (error) {
        console.warn('[TrackingProvider] localStorage unavailable, falling back to NoOp adapter:', error);
      }
    }

    // Final fallback: NoOp adapter
    console.warn('[TrackingProvider] No persistent storage available, using NoOp adapter');
    return new NoOpAdapter();
  };

  /**
   * Fetch configuration from backend
   * 
   * Requirement 1.2: Fetch configuration from backend on mount
   * Requirement 1.3: Use default config if fetch fails
   */
  const fetchConfig = async (): Promise<TrackingConfig> => {
    try {
      const endpoint = `${apiBaseUrl}/activity-tracking/v1/config?app_name=${encodeURIComponent(appName)}`;
      
      console.log('[TrackingProvider] Fetching config from:', endpoint);
      console.log('[TrackingProvider] Auth token:', typeof authToken === 'function' ? '[function - fetches fresh token]' : 'static string');
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': typeof authToken === 'function' ? await authToken() : (authToken || ''),
          'Content-Type': 'application/json',
        },
      });

      console.log('[TrackingProvider] Config response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read error');
        console.error('[TrackingProvider] Config fetch error:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText,
        });
        throw new Error(`Config fetch failed with status ${response.status}: ${errorText}`);
      }

      const fetchedConfig = await response.json();

      // Merge fetched config with defaults and provider props
      const mergedConfig: TrackingConfig = {
        enabled: fetchedConfig.enabled ?? DEFAULT_CONFIG.enabled,
        samplingRate: defaultSamplingRate ?? fetchedConfig.samplingRate ?? DEFAULT_CONFIG.samplingRate,
        rateLimit: fetchedConfig.rateLimit ?? DEFAULT_CONFIG.rateLimit,
        allowlist: fetchedConfig.allowlist ?? DEFAULT_CONFIG.allowlist,
        batchSize: batchSize ?? fetchedConfig.batchSize ?? DEFAULT_CONFIG.batchSize,
        flushInterval: flushInterval ?? fetchedConfig.flushInterval ?? DEFAULT_CONFIG.flushInterval,
      };

      console.log('[TrackingProvider] Configuration fetched successfully:', mergedConfig);
      return mergedConfig;
    } catch (error) {
      console.warn('[TrackingProvider] Failed to fetch configuration, using defaults:', error);
      
      // Return defaults with any provider overrides
      return {
        ...DEFAULT_CONFIG,
        samplingRate: defaultSamplingRate ?? DEFAULT_CONFIG.samplingRate,
        batchSize: batchSize ?? DEFAULT_CONFIG.batchSize,
        flushInterval: flushInterval ?? DEFAULT_CONFIG.flushInterval,
      };
    }
  };

  /**
   * Refresh configuration from backend
   * Can be called by consumers to reload config
   */
  const refreshConfig = async (): Promise<void> => {
    try {
      const newConfig = await fetchConfig();
      setConfig(newConfig);
      setIsEnabled(enabled && newConfig.enabled);
      
      // If batch manager exists and config changed, we may need to restart it
      // For now, log the change - actual reconfiguration can be added if needed
      console.log('[TrackingProvider] Configuration refreshed');
    } catch (error) {
      console.error('[TrackingProvider] Failed to refresh configuration:', error);
    }
  };

  /**
   * Initialize tracking SDK on mount
   * 
   * Requirement 1.1: Initialize EventQueue and BatchManager
   * Requirement 1.2: Fetch configuration from backend
   */
  useEffect(() => {
    // Prevent double initialization in strict mode
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    const initialize = async () => {
      try {
        // Step 1: Initialize EventQueue with storage adapter
        console.log('[TrackingProvider] Initializing EventQueue...');
        const eventQueue = await initializeQueue();
        setQueue(eventQueue);

        // Step 2: Fetch configuration from backend
        console.log('[TrackingProvider] Fetching configuration...');
        const fetchedConfig = await fetchConfig();
        setConfig(fetchedConfig);
        setIsEnabled(enabled && fetchedConfig.enabled);

        // Step 3: Create BatchManager with configuration
        console.log('[TrackingProvider] Creating BatchManager...');
        const batchManagerConfig: BatchManagerConfig = {
          batchSize: fetchedConfig.batchSize,
          flushInterval: fetchedConfig.flushInterval,
          apiBaseUrl,
          authToken,
        };

        const manager = new BatchManager(eventQueue, batchManagerConfig);
        setBatchManager(manager);

        // Step 4: Start batch manager
        if (enabled && fetchedConfig.enabled) {
          console.log('[TrackingProvider] Starting BatchManager...');
          manager.start();
        }

        console.log('[TrackingProvider] Initialization complete');
      } catch (error) {
        console.error('[TrackingProvider] Initialization failed:', error);
        
        // Set defaults even on failure for graceful degradation
        setConfig(DEFAULT_CONFIG);
        setIsEnabled(false);
      }
    };

    initialize();

    // Cleanup on unmount
    return () => {
      isUnmounting.current = true;
      
      if (batchManager) {
        console.log('[TrackingProvider] Stopping BatchManager...');
        batchManager.stop();
      }
    };
  }, []); // Empty dependency array - initialize only once

  /**
   * Context value provided to children
   */
  const contextValue: TrackingContextValue = {
    queue,
    batchManager,
    config,
    isEnabled,
    appName,
    userEnrichment,
    disabledEventTypes,
    refreshConfig,
  };

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
};
